import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faArrowRightArrowLeft } from '@fortawesome/sharp-solid-svg-icons'
import { Group } from '@mantine/core'
import lodash from 'lodash'
import React, { useEffect, useMemo } from 'react'
import { useForm } from 'react-final-form'

import Button from '../../../../../1stlog-ui/src/components/Button'
import Checkbox from '../../../../../1stlog-ui/src/components/Checkbox'
import Field, {
  FieldPrefix,
  useField,
} from '../../../../../1stlog-ui/src/components/Field'
import useFieldValue from '../../../../../1stlog-ui/src/components/Field/useFieldValue'
import AddressPickerAdvanced from '../../../AddressPickerAdvanced'
import Contact from '../../../AddressPickerAdvanced/Contact'
import ContactExpander from '../../../AddressPickerAdvanced/ContactExpander'
import AddressPickerAdvancedReadonly from '../../../AddressPickerAdvanced/Readonly'
import AddressSwitch from '../../components/AddressSwitch'
import OrderEntryCard, {
  IOrderEntryCardProps,
} from '../../components/OrderEntryCard'
import { useProductSettings } from '../../Products'
import useOrderEntry from '../../useOrderEntry'
import { getAddressPickerLabels } from '../../utils/getAddressPickerLabels'
import { useCustoms } from '../Customs'
import ReadonlyAddressCache from '../ReadonlyAddressCache'
import { useIsPhoneRequired } from './useIsPhoneRequired'

interface TPickupAddressProps
  extends Omit<IOrderEntryCardProps, 'title' | 'children'> {
  readonly?: boolean
}

const PickupAddress = (props: TPickupAddressProps): React.ReactElement => {
  const { readonly, ...rest } = props

  const { preset, currentCustomer } = useOrderEntry()

  // const { senderAddresses = [] } = currentCustomer ?? {}

  const { allowPickupAddressEdit, isPickupAddressStatic, isPickup } = preset

  const { isCrossingEu } = useCustoms()

  const country = useField('address.country')

  const addresses = useField('addresses')

  const [isEdit, setIsEdit] = useField('isEdit', { asState: true })
  const [, setAddress] = useField('address', { asState: true })

  const {
    eoriIsNeverMandatory,
    isPrenameSurnameContactVisible,
    isPickupPhoneAlwaysRequired,
    senderContactNameRequired,
  } = useProductSettings()

  // const { isPrenameSurnameContactVisible } = usePickupProduct() || {}

  const isPhoneRequiredGlobal = useIsPhoneRequired()
  const isPhoneRequired = isPickupPhoneAlwaysRequired || isPhoneRequiredGlobal

  // // auto set to edit if preset allows it
  // useEffect(() => {
  //   if (!isPickupAddressStatic) {
  //     setTimeout(() => setIsEdit(!!currentCustomer), 0)
  //   }
  // }, [isPickupAddressStatic])

  // read customerAddress in internal mode
  const customerAddress = useField('customer.address', { ignorePrefix: true })
  useEffect(() => {
    if (!isEdit && !currentCustomer) {
      setAddress(customerAddress)
    }
  }, [customerAddress, isEdit, currentCustomer])

  const showIsEdit = useMemo(
    () => (allowPickupAddressEdit && isPickupAddressStatic) || !currentCustomer,
    [allowPickupAddressEdit, isPickupAddressStatic, currentCustomer],
  )

  const isEoriRequired = !eoriIsNeverMandatory && isCrossingEu

  const isDeliveryAddressEdit = useFieldValue('delivery.isEdit', {
    ignorePrefix: true,
  })
  const isOwnDeliveryAddress = useFieldValue('delivery.isOwnDeliveryAddress', {
    ignorePrefix: true,
  })
  const form = useForm()
  const swapAddresses = (): void => {
    const { delivery, pickup } = form.getState().values
    setTimeout(() => {
      global.addressPickerSkipZipCityClear = 2
      form.batch(() => {
        form.change('delivery.address', pickup.address)
        form.change('pickup.address', delivery.address)
      })
    }, 0)
  }

  const showContact = useFieldValue('address.showContact')

  return (
    <OrderEntryCard
      {...rest}
      title={i18n.t('title_pickup_address', 'Abholadresse')}
    >
      {!readonly && showIsEdit && (
        <Group style={{ justifyContent: 'space-between' }}>
          <Field name="isEdit">
            <Checkbox
              label={i18n.t(
                'different_pickup_address_change_to_pickup_order2',
                'Abweichende Abholadresse / In Abholauftrag ändern',
              )}
              tooltip={i18n.t(
                'different_pickup_address_tooltip',
                'Klicken Sie hier, wenn Sie eine Abholung an einem anderen Ort durchführen lassen wollen. Beispielsweise eine Abholung einer Sendung bei einem Ihrer Kunden',
              )}
            />
          </Field>
          {isEdit && isDeliveryAddressEdit && !isOwnDeliveryAddress && (
            <div>
              <Button
                onClick={swapAddresses}
                small
                tooltip={i18n.t('swap_addresses', 'Adressen tauschen')}
              >
                <FontAwesomeIcon icon={faArrowRightArrowLeft} />
              </Button>
            </div>
          )}
        </Group>
      )}
      {!readonly && !isEdit && lodash.size(addresses) > 0 && (
        <AddressSwitch addresses={addresses} />
      )}
      <FieldPrefix prefix="address">
        {!readonly && isEdit ? (
          <AddressPickerAdvanced
            isEoriRequired={isEoriRequired}
            isPhoneRequired={isPhoneRequired}
            hidePrenameSurname={
              isPrenameSurnameContactVisible === false &&
              !senderContactNameRequired
            }
            labels={getAddressPickerLabels()}
            isNameRequired={senderContactNameRequired}
          />
        ) : (
          <AddressPickerAdvancedReadonly
            emptyText={i18n.t(
              'please_enter_customer_address_first',
              'Geben Sie zuerst eine Kundenadresse an',
            )}
          />
        )}
        {!readonly && !isEdit && (
          <>
            <div className="mt-2">
              <Field name="showContact">
                <Checkbox>
                  {i18n.t('showContact', 'Kontaktdaten einblenden')}
                </Checkbox>
              </Field>
            </div>
            <ContactExpander alwaysOpen={senderContactNameRequired} />
            <div hidden={!showContact}>
              <Contact
                isEoriRequired={isEoriRequired}
                isPhoneRequired={isPhoneRequired}
                hidePrenameSurname={isPrenameSurnameContactVisible === false}
                isNameRequired={senderContactNameRequired}
              />
            </div>
          </>
        )}
      </FieldPrefix>
      <ReadonlyAddressCache />
    </OrderEntryCard>
  )
}

export default PickupAddress
